import React, { useState, useEffect } from 'react';
import { Calendar, Clock, User, Mail, Phone, MapPin, CheckCircle, XCircle, AlertCircle, Filter, Search, Eye, Edit, Trash2 } from 'lucide-react';
import { useData } from '../../contexts/DataContext';
import { supabase, TABLES } from '../../config/supabase';
import logger from '../../utils/logger';

const BookingManagement = () => {
  const { leads } = useData();
  const [meetings, setMeetings] = useState([]);
  const [filteredMeetings, setFilteredMeetings] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedMeeting, setSelectedMeeting] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    completed: 0,
    cancelled: 0
  });

  // Load booking management data on component mount
  useEffect(() => {
    fetchBookingData();
  }, []);

  // Fetch booking management data from the new view
  const fetchBookingData = async () => {
    try {
      const { data, error } = await supabase
        .from('booking_management')
        .select('*')
        .order('meeting_date', { ascending: false });
      
      if (error) {
        logger.error('Error fetching booking data:', error);
        return;
      }
      
      setMeetings(data || []);
    } catch (error) {
      logger.error('Error fetching booking data:', error);
    }
  };

  // Filter and search meetings
  useEffect(() => {
    let filtered = meetings || [];

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(meeting => {
        const status = meeting.status || 'pending';
        return status === statusFilter;
      });
    }

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(meeting => 
        meeting.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        meeting.contact_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        meeting.contact_email?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredMeetings(filtered);

    // Calculate stats
    const totalMeetings = meetings || [];
    setStats({
      total: totalMeetings.length,
      pending: totalMeetings.filter(m => (m.status || 'pending') === 'pending').length,
      completed: totalMeetings.filter(m => (m.status || 'pending') === 'completed').length,
      cancelled: totalMeetings.filter(m => (m.status || 'pending') === 'cancelled').length
    });
  }, [meetings, statusFilter, searchTerm]);

  // Update meeting status
  const updateMeetingStatus = async (meetingId, newStatus) => {
    setLoading(true);
    try {
      logger.log('Attempting to update booking status:', { meetingId, newStatus });
      
      // Update or insert booking status in the separate table
      const { error } = await supabase
        .from('booking_status')
        .upsert({ 
          meeting_id: meetingId,
          status: newStatus,
          status_updated_by: 'user',
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'meeting_id'
        });

      if (error) {
        logger.error('Error updating meeting status:', error);
        alert('Failed to update meeting status: ' + error.message);
        return;
      }

      logger.log('Booking status updated successfully');
      // Refresh booking data
      await fetchBookingData();
      alert(`Meeting marked as ${newStatus}`);
    } catch (error) {
      logger.error('Error updating meeting:', error);
      alert('Failed to update meeting: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Delete meeting
  const deleteMeeting = async (meetingId) => {
    if (!window.confirm('Are you sure you want to delete this meeting?')) return;

    setLoading(true);
    try {
      // Delete the meeting (booking status will be deleted automatically due to CASCADE)
      const { error } = await supabase
        .from(TABLES.MEETINGS)
        .delete()
        .eq('id', meetingId);

      if (error) {
        logger.error('Error deleting meeting:', error);
        alert('Failed to delete meeting');
        return;
      }

      // Refresh booking data
      await fetchBookingData();
      alert('Meeting deleted successfully');
    } catch (error) {
      logger.error('Error deleting meeting:', error);
      alert('Failed to delete meeting');
    } finally {
      setLoading(false);
    }
  };

  // Get status badge
  const getStatusBadge = (status) => {
    const currentStatus = status || 'pending';
    switch (currentStatus) {
      case 'completed':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <CheckCircle className="w-3 h-3 mr-1" />
            Completed
          </span>
        );
      case 'cancelled':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
            <XCircle className="w-3 h-3 mr-1" />
            Cancelled
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            <AlertCircle className="w-3 h-3 mr-1" />
            Pending
          </span>
        );
    }
  };

  // Format date and time
  const formatDateTime = (date, time) => {
    const meetingDate = new Date(date);
    const formattedDate = meetingDate.toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
    
    // Format time
    const [hours, minutes] = time.split(':').map(Number);
    const timeDate = new Date();
    timeDate.setHours(hours, minutes, 0, 0);
    const formattedTime = timeDate.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });

    return `${formattedDate} at ${formattedTime}`;
  };

  // Meeting Detail Modal
  const MeetingModal = ({ meeting, onClose }) => {
    if (!meeting) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-xl font-semibold text-gray-900">{meeting.title}</h3>
                <p className="text-sm text-gray-500 mt-1">
                  {formatDateTime(meeting.meeting_date, meeting.meeting_time)}
                </p>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 p-1 rounded-md hover:bg-blue-50"
              >
                ×
              </button>
            </div>

            <div className="space-y-4">
              <div className="flex items-center space-x-2">
                <span className="text-sm font-medium text-gray-700">Status:</span>
                {getStatusBadge(meeting.status)}
              </div>

              {meeting.contact_name && (
                <div className="flex items-center space-x-2">
                  <User className="w-4 h-4 text-gray-500" />
                  <span className="text-sm text-gray-900">{meeting.contact_name}</span>
                </div>
              )}

              {meeting.contact_email && (
                <div className="flex items-center space-x-2">
                  <Mail className="w-4 h-4 text-gray-500" />
                  <span className="text-sm text-gray-900">{meeting.contact_email}</span>
                </div>
              )}

              {meeting.contact_phone && (
                <div className="flex items-center space-x-2">
                  <Phone className="w-4 h-4 text-gray-500" />
                  <span className="text-sm text-gray-900">{meeting.contact_phone}</span>
                </div>
              )}

              {meeting.meeting_type && (
                <div className="flex items-center space-x-2">
                  <MapPin className="w-4 h-4 text-gray-500" />
                  <span className="text-sm text-gray-900 capitalize">{meeting.meeting_type}</span>
                </div>
              )}

              {meeting.notes && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Notes:</h4>
                  <p className="text-sm text-gray-600 bg-blue-50 p-3 rounded-md">{meeting.notes}</p>
                </div>
              )}

              <div className="flex items-center space-x-2">
                <Clock className="w-4 h-4 text-blue-500" />
                <span className="text-sm text-gray-500">
                  Duration: {meeting.duration || 60} minutes
                </span>
              </div>
            </div>

            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={onClose}
                className="px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Booking Management</h1>
          <p className="text-gray-600">Manage and monitor all your meetings and bookings</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-blue-50 p-4 rounded-lg shadow-sm border border-blue-100">
          <div className="flex items-center">
            <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
              <Calendar className="w-5 h-5 text-white" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Total Meetings</p>
              <p className="text-xl font-bold text-blue-600">{stats.total}</p>
            </div>
          </div>
        </div>

        <div className="bg-blue-50 p-4 rounded-lg shadow-sm border border-blue-100">
          <div className="flex items-center">
            <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
              <AlertCircle className="w-5 h-5 text-white" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Pending</p>
              <p className="text-xl font-bold text-blue-600">{stats.pending}</p>
            </div>
          </div>
        </div>

        <div className="bg-blue-50 p-4 rounded-lg shadow-sm border border-blue-100">
          <div className="flex items-center">
            <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-white" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Completed</p>
              <p className="text-xl font-bold text-blue-600">{stats.completed}</p>
            </div>
          </div>
        </div>

        <div className="bg-blue-50 p-4 rounded-lg shadow-sm border border-blue-100">
          <div className="flex items-center">
            <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
              <XCircle className="w-5 h-5 text-white" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Cancelled</p>
              <p className="text-xl font-bold text-blue-600">{stats.cancelled}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-blue-50 p-4 rounded-lg shadow-sm border border-blue-100">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-3 md:space-y-0">
          <div className="flex items-center space-x-3">
            <div className="flex items-center space-x-2">
              <Filter className="w-4 h-4 text-blue-500" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="border border-blue-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-gray-900"
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <Search className="w-4 h-4 text-blue-500" />
            <input
              type="text"
              placeholder="Search meetings..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="border border-blue-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-gray-900 w-64"
            />
          </div>
        </div>
      </div>

      {/* Meetings Table */}
      <div className="bg-blue-50 rounded-lg shadow-sm border border-blue-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-blue-200">
            <thead className="bg-white">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                  Meeting
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                  Contact
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                  Date & Time
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-blue-100">
              {filteredMeetings.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-4 py-8 text-center text-gray-500">
                    <Calendar className="w-10 h-10 text-blue-300 mx-auto mb-3" />
                    <p className="text-base font-medium">No meetings found</p>
                    <p className="text-sm">Try adjusting your filters or search terms</p>
                  </td>
                </tr>
              ) : (
                filteredMeetings.map((meeting) => (
                  <tr key={meeting.id} className="hover:bg-blue-50">
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div>
                        <div 
                          className="text-sm font-medium text-gray-900 hover:text-blue-600 cursor-pointer transition-colors"
                          onClick={() => {
                            // Find associated lead and navigate
                            const associatedLead = leads.find(lead => 
                              lead.email === meeting.contact_email || 
                              lead.name === meeting.contact_name
                            );
                            if (associatedLead) {
                              // Dispatch event to navigate to lead
                              window.dispatchEvent(new CustomEvent('viewLead', {
                                detail: { lead: associatedLead }
                              }));
                            } else {
                              alert('Associated lead not found');
                            }
                          }}
                          title="Click to view associated lead"
                        >
                          {meeting.title || 'Untitled Meeting'}
                        </div>
                        <div className="text-sm text-gray-500">
                          {meeting.meeting_type && (
                            <span className="capitalize">{meeting.meeting_type}</span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {meeting.contact_name || 'N/A'}
                        </div>
                        <div className="text-sm text-gray-500">
                          {meeting.contact_email || 'N/A'}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {formatDateTime(meeting.meeting_date, meeting.meeting_time)}
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {getStatusBadge(meeting.status)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => {
                            setSelectedMeeting(meeting);
                            setShowModal(true);
                          }}
                          className="text-blue-600 hover:text-blue-900 p-1 rounded-md hover:bg-blue-50"
                          title="View Details"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        
                        {/* Always show status update buttons for pending meetings */}
                        {(meeting.status || 'pending') === 'pending' && (
                          <>
                            <button
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                logger.log('Marking meeting as completed:', meeting.id);
                                updateMeetingStatus(meeting.id, 'completed');
                              }}
                              className="text-green-600 hover:text-green-900 p-2 rounded-md hover:bg-green-50 transition-colors"
                              title="Mark as Completed"
                              disabled={loading}
                            >
                              <CheckCircle className="w-4 h-4" />
                            </button>
                            <button
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                logger.log('Marking meeting as cancelled:', meeting.id);
                                updateMeetingStatus(meeting.id, 'cancelled');
                              }}
                              className="text-red-600 hover:text-red-900 p-2 rounded-md hover:bg-red-50 transition-colors"
                              title="Cancel Meeting"
                              disabled={loading}
                            >
                              <XCircle className="w-4 h-4" />
                            </button>
                          </>
                        )}
                        
                        {/* Show status for completed/cancelled meetings */}
                        {(meeting.status === 'completed' || meeting.status === 'cancelled') && (
                          <span className="text-xs text-gray-500 italic">
                            {meeting.status === 'completed' ? 'Completed' : 'Cancelled'}
                          </span>
                        )}
                        
                        <button
                          onClick={() => deleteMeeting(meeting.id)}
                          className="text-red-600 hover:text-red-900 p-1 rounded-md hover:bg-red-50"
                          title="Delete Meeting"
                          disabled={loading}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Meeting Detail Modal */}
      {showModal && selectedMeeting && (
        <MeetingModal 
          meeting={selectedMeeting} 
          onClose={() => {
            setShowModal(false);
            setSelectedMeeting(null);
          }} 
        />
      )}
    </div>
  );
};

export default BookingManagement;